import React, { useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Eye, Code, Type, FileText } from 'lucide-react';
import { motion } from 'framer-motion';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  contentType: 'rich_text' | 'html' | 'markdown' | 'structured_data';
  onContentTypeChange: (type: 'rich_text' | 'html' | 'markdown' | 'structured_data') => void;
  placeholder?: string;
  label?: string;
  className?: string;
}

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'font': [] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'direction': 'rtl' }],
    [{ 'align': [] }],
    ['link', 'image', 'video', 'code-block'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false,
  }
};

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video', 'code-block',
  'color', 'background',
  'align', 'direction',
  'script'
];

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  contentType,
  onContentTypeChange,
  placeholder = "Start writing...",
  label,
  className
}) => {
  const quillRef = useRef<ReactQuill>(null);

  const renderEditor = () => {
    switch (contentType) {
      case 'rich_text':
        return (
          <div className="relative">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={value}
              onChange={onChange}
              modules={modules}
              formats={formats}
              placeholder={placeholder}
              className={cn(
                "bg-background border-2 border-border rounded-lg overflow-hidden",
                "focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20",
                "[&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-border",
                "[&_.ql-container]:border-0 [&_.ql-container]:font-sans",
                "[&_.ql-editor]:min-h-[200px] [&_.ql-editor]:text-foreground",
                "[&_.ql-editor.ql-blank::before]:text-muted-foreground",
                className
              )}
              style={{
                background: 'hsl(var(--background))',
              }}
            />
          </div>
        );
      
      case 'html':
      case 'markdown':
      case 'structured_data':
        return (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={cn(
              "w-full min-h-[200px] p-4 border-2 border-border rounded-lg",
              "bg-background text-foreground font-mono text-sm",
              "focus:border-primary/50 focus:ring-2 focus:ring-primary/20 focus:outline-none",
              "placeholder:text-muted-foreground resize-y",
              className
            )}
          />
        );
      
      default:
        return null;
    }
  };

  const contentTypeOptions = [
    { value: 'rich_text', label: 'Rich Text', icon: Type, description: 'WYSIWYG editor with formatting' },
    { value: 'html', label: 'HTML', icon: Code, description: 'Raw HTML code' },
    { value: 'markdown', label: 'Markdown', icon: FileText, description: 'Markdown syntax' },
    { value: 'structured_data', label: 'JSON', icon: FileText, description: 'Structured JSON data' }
  ];

  return (
    <motion.div 
      className="space-y-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {label && (
        <Label className="text-base font-semibold text-foreground">{label}</Label>
      )}
      
      <Tabs value={contentType} onValueChange={(value) => onContentTypeChange(value as any)}>
        <TabsList className="grid w-full grid-cols-4 bg-muted/50 backdrop-blur-sm">
          {contentTypeOptions.map((option) => (
            <TabsTrigger 
              key={option.value} 
              value={option.value}
              className="data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
            >
              <option.icon className="h-4 w-4 mr-2" />
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {contentTypeOptions.map((option) => (
          <TabsContent key={option.value} value={option.value} className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center gap-2">
                <option.icon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{option.label}</span>
                <span className="text-xs text-muted-foreground">- {option.description}</span>
              </div>
              {contentType === 'rich_text' && (
                <Button variant="ghost" size="sm" className="h-8">
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Button>
              )}
            </div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              className="relative"
            >
              {renderEditor()}
            </motion.div>
          </TabsContent>
        ))}
      </Tabs>
    </motion.div>
  );
};

export default RichTextEditor;